/**
 * ============================================================================
 * BUDGET CATEGORIES v3.0 - CARD LAYOUT (wie Transactions)
 * ============================================================================
 *
 * ✅ v3.0: CARD LAYOUT
 *          - Outer card wrapper (white bg, border, rounded corners)
 *          - Card header: title left + optional add button right
 *          - Content: sections + grid inside card
 * ✅ v2.0: Planivio Branding + Fix Kategorie-Typ
 *
 * @version 3.0.0
 */

const TEAL = '#4A90A4';
const GREEN = '#6BA893';
const BORDER = '#B8D4DC';
const BORDER_LIGHT = '#D4E8EC';
const BG_LIGHT = '#E8F4F8';

// ── Card layout styles (same as BudgetTransactions) ────────────────────────
const card = {
  background: 'white',
  borderRadius: '20px',
  border: `2px solid ${BORDER}`,
  boxShadow: '0 2px 8px rgba(74,144,164,0.1)',
  overflow: 'hidden'
};
const cardHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.45rem 1.25rem',
  borderBottom: `1px solid ${BORDER_LIGHT}`,
  background: BG_LIGHT
};
const cardTitle = {
  margin: 0,
  fontSize: '1.1rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  color: '#1F2937'
};
const cardContent = {
  padding: '1.25rem 1.5rem'
};

function BudgetCategories({ categories, onEdit, onDelete, onAdd }) {

  const formatCurrency = (amount) => new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);

  const fixedCategories = categories.filter(c => c.is_fixed === true);
  const variableCategories = categories.filter(c => !c.is_fixed);

  return (
    <div style={card}>

      {/* Card Header */}
      <div style={cardHeader}>
        <h2 style={cardTitle}><span>🏷️</span> Kategorien</h2>
      </div>

      {/* Card Content */}
      <div style={cardContent}>
        {categories.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#6B7280'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🏷️</div>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#1F2937' }}>
              Noch keine Kategorien
            </p>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Erstelle Kategorien für Fixkosten und variable Ausgaben
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Fixkosten */}
            {fixedCategories.length > 0 && (
              <div>
                <h3 style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: TEAL,
                  margin: '0 0 0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  📌 Fixkosten ({fixedCategories.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '0.75rem'
                }}>
                  {fixedCategories.map(cat => (
                    <CategoryCard
                      key={cat.id}
                      category={cat}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Variable Kosten */}
            {variableCategories.length > 0 && (
              <div>
                <h3 style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: GREEN,
                  margin: '0 0 0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  🔄 Variable Kosten ({variableCategories.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: '0.75rem'
                }}>
                  {variableCategories.map(cat => (
                    <CategoryCard
                      key={cat.id}
                      category={cat}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS for category cards */}
      <style>{`
        .budget-category-card {
          background: white;
          border-radius: 10px;
          border: 1px solid ${BORDER};
          border-left-width: 3px;
          padding: 0.45rem 0.75rem;
          transition: all 0.2s ease;
        }
        .budget-category-card:hover {
          border-color: ${TEAL};
          border-left-color: var(--cat-color, ${TEAL});
          box-shadow: 0 2px 8px rgba(74,144,164,0.1);
          transform: translateY(-1px);
        }
        .budget-category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .budget-category-main {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .budget-category-icon {
          width: 28px;
          height: 28px;
          border-radius: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.95rem;
          flex-shrink: 0;
        }
        .budget-category-name {
          margin: 0 0 0.1rem;
          font-size: 0.82rem;
          font-weight: 600;
          color: #1F2937;
        }
        .budget-category-tag {
          display: inline-flex;
          align-items: center;
          padding: 0.05rem 0.35rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.58rem;
          text-transform: uppercase;
        }
        .budget-category-tag.fixed {
          background: rgba(74,144,164,0.1);
          color: ${TEAL};
        }
        .budget-category-tag.variable {
          background: rgba(107,168,147,0.1);
          color: ${GREEN};
        }
        .budget-category-actions {
          display: flex;
          gap: 0.25rem;
        }
        .budget-btn-icon {
          width: 32px;
          height: 32px;
          border: 1px solid ${BORDER};
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          transition: all 0.2s;
          position: relative;
          padding: 0;
        }
        .budget-btn-icon::before {
          content: '';
          position: absolute;
          inset: -6px;
        }
        .budget-btn-icon:hover { background: ${BG_LIGHT}; }
        .budget-btn-icon.budget-btn-danger:hover { background: #FEE2E2; border-color: #FECACA; }
        .budget-category-limit {
          margin-top: 0.3rem;
          padding-top: 0.3rem;
          border-top: 1px solid ${BORDER_LIGHT};
          font-size: 0.72rem;
          color: #6B7280;
        }
      `}</style>

    </div>
  );
}

function CategoryCard({ category: cat, onEdit, onDelete, formatCurrency }) {
  return (
    <div
      className="budget-category-card"
      style={{ borderLeftColor: cat.color || TEAL }}
    >
      <div className="budget-category-header">
        <div className="budget-category-main">
          <div
            className="budget-category-icon"
            style={{ background: `${cat.color || TEAL}18` }}
          >
            {cat.icon || '📁'}
          </div>
          <div>
            <h3 className="budget-category-name">{cat.name}</h3>
            <span className={`budget-category-tag ${cat.is_fixed ? 'fixed' : 'variable'}`}>
              {cat.is_fixed ? '📌 Fix' : '🔄 Var'}
            </span>
          </div>
        </div>

        <div className="budget-category-actions">
          <button
            onClick={() => onEdit(cat)}
            className="budget-btn-icon"
            title="Bearbeiten"
          >✏️</button>
          <button
            onClick={() => onDelete(cat.id)}
            className="budget-btn-icon budget-btn-danger"
            title="Löschen"
          >🗑️</button>
        </div>
      </div>

      {cat.monthly_limit && (
        <div className="budget-category-limit">
          🎯 Monatslimit: {formatCurrency(cat.monthly_limit)}
        </div>
      )}
    </div>
  );
}

export default BudgetCategories;
